import * as d3 from "d3";
import { getTheme } from "@semantical/theme.ts";
import React from "react";
import DiagramRelationship from "@semantical/modules/diagram/DiagramRelationship.ts";
import DiagramClass from "@semantical/modules/diagram/DiagramClass.ts";
import { RELATIONSHIP_TYPE, getRelationshipStyle } from "@semantical/modules/knowledge-graph";
import { AnchorPosition, AnchorMode, constrainToClassBorder } from "@semantical/modules/diagram/AnchorPosition.ts";
import type { RelationshipStyle } from "@semantical/modules/diagram/Diagram.ts";

// Default marker for relationships without visual annotations
const DEFAULT_TARGET_MARKER = "openArrowhead";

// Legacy marker support
export const legacyMarkers: Map<string, string> = new Map([
    ["COMPOSITION", "compositeDiamond"],
    ["AGGREGATION", "sharedDiamond"],
    ["GENERALIZATION", "generalizationArrowhead"]
]);

// Get stroke dasharray for relationship type
export function getStrokeDasharray(type: RELATIONSHIP_TYPE): string {
    const style = getRelationshipStyle(type);
    switch (style.strokeStyle) {
        case "dashed":
            return "8,4";
        case "dotted":
            return "2,3";
        default:
            return "none";
    }
}

type Point = {
    x: number; y: number;
};

type Props = {
    relationship: DiagramRelationship;
    onRelationshipSelected?: (relationship: DiagramRelationship) => void;
    onAnchorMoved?: (relationship: DiagramRelationship, isSource: boolean, anchor: AnchorPosition) => void;
    onAnchorReconnected?: (relationship: DiagramRelationship, isSource: boolean, newClass: DiagramClass) => void;
    highlightedClass?: DiagramClass | null;
    selected?: boolean;
    // All classes on the diagram for reconnection detection
    allClasses?: DiagramClass[];
    // Callback to highlight a class during reconnection drag
    onClassHover?: (diagramClass: DiagramClass | null) => void;
    // Callback to check if reconnecting to a class is compatible with domain/range constraints
    checkReconnectCompatibility?: (newClass: DiagramClass, isSource: boolean) => boolean;
    // Edge index for multiple edges between same node pair (for visual separation)
    edgeIndex?: number;
    edgeTotal?: number;
    // Whether this relationship was inferred (read-only, different color)
    isInferred?: boolean;
    // Relationship style: 's-curve' (default) or 'arc'
    relationshipStyle?: RelationshipStyle;
    // Anchor positioning mode: 'auto', 'dynamic', or 'centered'
    anchorMode?: AnchorMode;
    // URI of the currently selected node (for highlighting outgoing edges)
    selectedNodeUri?: string | null;
    // Map of predicate URIs to highlight colors for WordCloud feature
    highlightedPredicates?: Map<string, string>;
}

// Color for inferred relationships
const INFERRED_RELATIONSHIP_COLOR = "#9333EA";  // Purple

type State = {
    sourceAnchorHovered: boolean;
    targetAnchorHovered: boolean;
    draggingAnchor: 'source' | 'target' | null;
    relationshipHovered: boolean;
    // Reconnection mode state
    reconnectMode: boolean;
    reconnectTargetClass: DiagramClass | null;
    reconnectCursorPosition: Point | null;
    // Whether the current reconnect target is compatible with constraints
    reconnectIsCompatible: boolean;
}

export class DiagramRelationshipElement extends React.Component<Props, State> {
    // private
    svgRelationship: React.RefObject<SVGGElement | null> = React.createRef<SVGGElement>();
    sourceAnchorRef: React.RefObject<SVGGElement | null> = React.createRef<SVGGElement>();
    targetAnchorRef: React.RefObject<SVGGElement | null> = React.createRef<SVGGElement>();

    // Drag tracking for distinguishing click vs drag
    private dragStartPosition: { x: number; y: number } | null = null;
    private dragDistance = 0;
    private static readonly DRAG_THRESHOLD = 5; // pixels

    // Anchor visual constants
    static readonly ANCHOR_RADIUS = 6;
    static readonly ANCHOR_GLOW_RADIUS = 12;

    constructor(props: Props) {
        super(props);

        this.svgRelationship = React.createRef<SVGGElement>();
        this.sourceAnchorRef = React.createRef<SVGGElement>();
        this.targetAnchorRef = React.createRef<SVGGElement>();

        this.state = {
            sourceAnchorHovered: false,
            targetAnchorHovered: false,
            draggingAnchor: null,
            relationshipHovered: false,
            reconnectMode: false,
            reconnectTargetClass: null,
            reconnectCursorPosition: null,
            reconnectIsCompatible: true
        };
    }

    getMarkers(): { sourceMarker: string | null; targetMarker: string | null } {
        const predicate = this.props.relationship.modelRelationship.predicate;
        const type = this.props.relationship.modelRelationship.type;

        // Handle legacy uppercase types (backward compatibility)
        if (legacyMarkers.has(type as string)) {
            return { sourceMarker: legacyMarkers.get(type as string) || null, targetMarker: null };
        }

        // Read markers from the predicate's visual annotations (sem:sourceMarker, sem:targetMarker)
        const sourceMarker = predicate.sourceMarker || null;
        const targetMarker = predicate.targetMarker || null;

        // If no markers defined in ontology, use default open arrowhead at target
        if (!sourceMarker && !targetMarker) {
            return { sourceMarker: null, targetMarker: DEFAULT_TARGET_MARKER };
        }

        return { sourceMarker, targetMarker };
    }

    renderAnchor(isSource: boolean) {
        const theme = getTheme();
        const hovered = isSource ? this.state.sourceAnchorHovered : this.state.targetAnchorHovered;
        const ref = isSource ? this.sourceAnchorRef : this.targetAnchorRef;
        const className = isSource ? 'sourceAnchor' : 'targetAnchor';
        // Keep anchors visible during drag
        const shouldShow = this.props.selected || this.state.relationshipHovered || this.state.draggingAnchor !== null;
        // Check if this anchor has a fixed position
        const relationship = this.props.relationship;
        const isFixed = isSource ? relationship.sourceAnchor !== null : relationship.targetAnchor !== null;

        return (
            <g ref={ref} className={`anchor ${className}`} style={{ cursor: 'move', opacity: shouldShow ? 1 : 0 }}>
                {/* Glow circle (visible on hover/selection) */}
                <circle
                    className="anchor-glow"
                    r={DiagramRelationshipElement.ANCHOR_GLOW_RADIUS}
                    fill={theme.anchorGlow}
                    opacity={hovered || this.props.selected ? 0.6 : 0}
                    style={{ pointerEvents: 'none' }}
                />
                {/* Main anchor circle */}
                <circle
                    className="anchor-visual"
                    r={DiagramRelationshipElement.ANCHOR_RADIUS}
                    fill={hovered ? theme.anchorHoverFill : theme.anchorFill}
                    stroke={hovered ? theme.anchorHoverStroke : theme.anchorStroke}
                    strokeWidth={1.5}
                />
                {/* Fixed position indicator - small inner diamond */}
                {isFixed && (
                    <rect
                        className="anchor-fixed-indicator"
                        x={-2.5}
                        y={-2.5}
                        width={5}
                        height={5}
                        fill={hovered ? theme.anchorHoverStroke : theme.anchorStroke}
                        transform="rotate(45)"
                        style={{ pointerEvents: 'none' }}
                    />
                )}
                {/* Hit area (larger invisible circle for easier interaction) */}
                <circle
                    className="anchor-hit"
                    r={DiagramRelationshipElement.ANCHOR_RADIUS * 2}
                    fill="transparent"
                    style={{ pointerEvents: 'all' }}
                />
            </g>
        );
    }

    render() {
        const theme = getTheme();
        const type = this.props.relationship.modelRelationship.type;
        const label = this.props.relationship.modelRelationship.name;
        const { sourceMarker, targetMarker } = this.getMarkers();
        const dasharray = getStrokeDasharray(type as RELATIONSHIP_TYPE);

        return (
            <g ref={this.svgRelationship} className="diagramRelationship" data-relationship-type={type}>
                {/* Main visual path */}
                <path className="visual" strokeWidth={2} fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      markerStart={sourceMarker ? `url(#${sourceMarker})` : undefined}
                      markerEnd={targetMarker ? `url(#${targetMarker})` : undefined}
                      strokeDasharray={dasharray !== "none" ? dasharray : undefined}
                      style={{ pointerEvents: 'none' }}/>
                {/* Hit area for interaction */}
                <path className="hit" strokeWidth={15} fill="none" stroke="transparent" style={{ pointerEvents: 'stroke' }}/>
                {/* Relationship label */}
                <g className="relationship-label" style={{ cursor: 'pointer' }}>
                    <rect className="label-background" rx={6} ry={6} fill="rgba(40, 42, 50, 0.9)" />
                    <text className="label-text" textAnchor="middle" dominantBaseline="middle"
                          style={{ fontFamily: "'Inter', 'SF Pro Text', -apple-system, sans-serif", fontSize: '10px', fill: 'rgba(255, 255, 255, 0.75)', fontWeight: 500, letterSpacing: '0.3px', pointerEvents: 'none' }}>
                        {label}
                    </text>
                </g>
                {/* Anchor points (hidden for inferred relationships and arc style) */}
                {!this.props.isInferred && this.props.relationshipStyle !== 'arc' && this.renderAnchor(true)}
                {!this.props.isInferred && this.props.relationshipStyle !== 'arc' && this.renderAnchor(false)}
                {/* Ghost line for reconnection mode */}
                {this.state.reconnectMode && this.state.reconnectCursorPosition && (
                    <path
                        className="reconnect-ghost-line"
                        stroke={this.state.reconnectIsCompatible ? theme.lineHighlight : "rgba(255, 140, 60, 1)"}
                        strokeWidth={2}
                        strokeDasharray="6,4"
                        fill="none"
                        opacity={0.8}
                        style={{ pointerEvents: 'none' }}
                    />
                )}
            </g>
        );
    }

    handleMouseOver(_e: MouseEvent) {
        this.setState({ relationshipHovered: true });
        this.highlight();
        this.updateAnchorsVisibility(true);
    }

    handleMouseOut(e: MouseEvent) {
        // Check if mouse is moving to an anchor (relatedTarget is where the mouse is going)
        const relatedTarget = e.relatedTarget as Element | null;
        if (relatedTarget) {
            // Check if the related target is inside our relationship group (includes anchors)
            const relationshipGroup = this.svgRelationship.current;
            if (relationshipGroup && relationshipGroup.contains(relatedTarget)) {
                // Mouse is moving to an anchor or other part of this relationship, don't hide
                return;
            }
        }

        // Don't hide if an anchor is being dragged
        if (this.state.draggingAnchor) {
            return;
        }

        this.setState({ relationshipHovered: false });
        this.refresh();
        if (!this.props.selected) {
            this.updateAnchorsVisibility(false);
        }
    }

    updateAnchorsVisibility(visible: boolean, disableInteraction: boolean = false) {
        const opacity = visible ? 1 : 0;
        const pointerEvents = disableInteraction ? "none" : "auto";

        d3.select(this.sourceAnchorRef.current)
            .transition()
            .duration(200)
            .style("opacity", opacity)
            .style("pointer-events", pointerEvents);

        d3.select(this.targetAnchorRef.current)
            .transition()
            .duration(200)
            .style("opacity", opacity)
            .style("pointer-events", pointerEvents);
    }

    handleClick(_e: MouseEvent) {
        if (this.props.onRelationshipSelected)
            this.props.onRelationshipSelected(this.props.relationship);
    }

    componentDidMount() {
        console.debug("componentDidMount DiagramRelationshipElement");

        // Skip interactive setup for inferred relationships (they are read-only)
        if (!this.props.isInferred) {
            const hit = d3.select(this.svgRelationship.current).select("path.hit");

            hit.on("mouseover", (e) => this.handleMouseOver(e))
                .on("mouseout", (e) => this.handleMouseOut(e))
                .on("click", (e) => this.handleClick(e));

            // Set up label interactions (click to select, hover to highlight)
            const labelGroup = d3.select(this.svgRelationship.current).select("g.relationship-label");
            labelGroup
                .on("mouseover", (e) => this.handleMouseOver(e))
                .on("mouseout", (e) => this.handleMouseOut(e))
                .on("click", (e) => this.handleClick(e));

            // Set up anchor interactions
            this.setupAnchorInteractions(true);
            this.setupAnchorInteractions(false);
        }

        this.refresh();
    }

    setupAnchorInteractions(isSource: boolean) {
        const ref = isSource ? this.sourceAnchorRef : this.targetAnchorRef;
        const anchorGroup = d3.select(ref.current);

        // Hover events
        anchorGroup
            .on("mouseenter", () => {
                if (isSource) {
                    this.setState({ sourceAnchorHovered: true });
                } else {
                    this.setState({ targetAnchorHovered: true });
                }
                this.updateAnchorVisuals(isSource, true);
            })
            .on("mouseleave", (event: MouseEvent) => {
                if (this.state.draggingAnchor !== (isSource ? 'source' : 'target')) {
                    // Check if we're leaving the relationship entirely (not going to the line or other anchor)
                    const relatedTarget = event.relatedTarget as Element | null;
                    const relationshipGroup = this.svgRelationship.current;
                    const stayingInRelationship = relatedTarget && relationshipGroup && relationshipGroup.contains(relatedTarget);

                    if (isSource) {
                        this.setState({
                            sourceAnchorHovered: false,
                            // Reset relationshipHovered if leaving entirely
                            relationshipHovered: stayingInRelationship ? this.state.relationshipHovered : false
                        }, () => {
                            // Check if we should hide all anchors
                            if (!this.state.relationshipHovered && !this.state.targetAnchorHovered && !this.props.selected) {
                                this.updateAnchorsVisibility(false);
                            }
                        });
                    } else {
                        this.setState({
                            targetAnchorHovered: false,
                            // Reset relationshipHovered if leaving entirely
                            relationshipHovered: stayingInRelationship ? this.state.relationshipHovered : false
                        }, () => {
                            // Check if we should hide all anchors
                            if (!this.state.relationshipHovered && !this.state.sourceAnchorHovered && !this.props.selected) {
                                this.updateAnchorsVisibility(false);
                            }
                        });
                    }
                    this.updateAnchorVisuals(isSource, false);
                }
            });

        // Drag behavior - use clickDistance to allow double-click detection
        const dragBehavior = d3.drag<SVGGElement, unknown>()
            .clickDistance(5) // Allow small movements without triggering drag, enabling double-click
            .on("start", (event) => this.handleAnchorDragStart(event, isSource))
            .on("drag", (event) => this.handleAnchorDrag(event, isSource))
            .on("end", (event) => this.handleAnchorDragEnd(event, isSource));

        if (ref.current) {
            d3.select(ref.current)
                .call(dragBehavior)
                .on("dblclick", (event) => this.handleAnchorDoubleClick(event, isSource));
        }
    }

    handleAnchorDoubleClick(event: MouseEvent, isSource: boolean) {
        // Stop propagation to prevent zoom reset on the diagram
        event.stopPropagation();
        event.preventDefault();

        // Disable double-click reset for arc style (fixed positions not supported)
        if (this.props.relationshipStyle === 'arc') {
            return;
        }

        console.debug(`Anchor double-click: ${isSource ? 'source' : 'target'} - resetting to default`);

        const relationship = this.props.relationship;

        // Reset the anchor to null (will use computed default)
        if (isSource) {
            relationship.sourceAnchor = null;
        } else {
            relationship.targetAnchor = null;
        }

        // Notify parent of anchor change
        if (this.props.onAnchorMoved) {
            const newAnchor = isSource ? relationship.getSourceAnchor() : relationship.getTargetAnchor();
            this.props.onAnchorMoved(relationship, isSource, newAnchor);
        }

        this.refresh();
    }

    updateAnchorVisuals(isSource: boolean, hovered: boolean) {
        const theme = getTheme();
        const ref = isSource ? this.sourceAnchorRef : this.targetAnchorRef;
        const anchorGroup = d3.select(ref.current);

        // Update glow
        anchorGroup.select("circle.anchor-glow")
            .transition()
            .duration(200)
            .attr("opacity", hovered || this.props.selected ? 0.6 : 0);

        // Update visual circle
        anchorGroup.select("circle.anchor-visual")
            .transition()
            .duration(200)
            .attr("fill", hovered ? theme.anchorHoverFill : theme.anchorFill)
            .attr("stroke", hovered ? theme.anchorHoverStroke : theme.anchorStroke);
    }

    /**
     * Check if a point is inside a class's bounding box (with optional margin)
     */
    isPointInsideClass(x: number, y: number, diagramClass: DiagramClass, margin: number = 20): boolean {
        return x >= diagramClass.x - margin &&
               x <= diagramClass.x + diagramClass.width + margin &&
               y >= diagramClass.y - margin &&
               y <= diagramClass.y + diagramClass.height + margin;
    }

    /**
     * Find which class (if any) contains the given point
     */
    findClassAtPoint(x: number, y: number, excludeClass?: DiagramClass): DiagramClass | null {
        if (!this.props.allClasses) return null;

        for (const diagramClass of this.props.allClasses) {
            // Don't match the excluded class (typically the current anchor's class)
            if (excludeClass && diagramClass === excludeClass) continue;

            if (this.isPointInsideClass(x, y, diagramClass, 0)) {
                return diagramClass;
            }
        }
        return null;
    }

    /**
     * Update the ghost line path during reconnection mode
     */
    updateGhostLine(fromAnchor: AnchorPosition, toPoint: Point) {
        const ghostLine = d3.select(this.svgRelationship.current).select("path.reconnect-ghost-line");
        if (!ghostLine.empty()) {
            const d = `M ${fromAnchor.x} ${fromAnchor.y} L ${toPoint.x} ${toPoint.y}`;
            ghostLine.attr("d", d);
        }
    }

    handleAnchorDragStart(event: d3.D3DragEvent<SVGGElement, unknown, unknown>, isSource: boolean) {
        // Disable anchor dragging for arc style (fixed positions not supported)
        if (this.props.relationshipStyle === 'arc') {
            return;
        }

        console.debug(`Anchor drag start: ${isSource ? 'source' : 'target'}`);

        // Track start position for drag distance calculation
        this.dragStartPosition = { x: event.x, y: event.y };
        this.dragDistance = 0;

        this.setState({
            draggingAnchor: isSource ? 'source' : 'target',
            reconnectMode: false,
            reconnectTargetClass: null,
            reconnectCursorPosition: null,
            reconnectIsCompatible: true
        });
    }

    handleAnchorDrag(event: d3.D3DragEvent<SVGGElement, unknown, unknown>, isSource: boolean) {
        const relationship = this.props.relationship;
        const currentClass = isSource ? relationship.source : relationship.target;
        const otherClass = isSource ? relationship.target : relationship.source;

        // D3 drag event.x and event.y are already in the correct coordinate system
        const x = event.x;
        const y = event.y;

        // Track drag distance from start position
        if (this.dragStartPosition) {
            const dx = x - this.dragStartPosition.x;
            const dy = y - this.dragStartPosition.y;
            this.dragDistance = Math.sqrt(dx * dx + dy * dy);
        }

        // Check if cursor is inside the current class (reposition mode)
        const insideCurrentClass = this.isPointInsideClass(x, y, currentClass, 30);

        if (insideCurrentClass) {
            // REPOSITION MODE: constrain anchor to current class border
            if (this.state.reconnectMode) {
                // Exiting reconnect mode, clear highlights and restore main path
                this.props.onClassHover?.(null);
                this.setState({
                    reconnectMode: false,
                    reconnectTargetClass: null,
                    reconnectCursorPosition: null,
                    reconnectIsCompatible: true
                });
                // Restore main relationship path visibility
                d3.select(this.svgRelationship.current).select("path.visual")
                    .style("opacity", 1);
                d3.select(this.svgRelationship.current).select("path.hit")
                    .style("opacity", 1);
                d3.select(this.svgRelationship.current).select("g.relationship-label")
                    .style("opacity", 1);
            }

            const newAnchor = constrainToClassBorder(
                x, y,
                currentClass.x, currentClass.y,
                currentClass.width, currentClass.height
            );

            // Update anchor position visually during drag
            const ref = isSource ? this.sourceAnchorRef : this.targetAnchorRef;
            d3.select(ref.current)
                .attr("transform", `translate(${newAnchor.x}, ${newAnchor.y})`);

            // Update path during drag
            this.updatePathDuringDrag(isSource, newAnchor);

            // Hide ghost line
            d3.select(this.svgRelationship.current).select("path.reconnect-ghost-line")
                .attr("d", null);
        } else {
            // RECONNECT MODE: anchor follows cursor, look for target classes
            if (!this.state.reconnectMode) {
                this.setState({ reconnectMode: true });
                // Hide the main relationship path - only show ghost line
                d3.select(this.svgRelationship.current).select("path.visual")
                    .style("opacity", 0);
                d3.select(this.svgRelationship.current).select("path.hit")
                    .style("opacity", 0);
                // Also hide the label during reconnection
                d3.select(this.svgRelationship.current).select("g.relationship-label")
                    .style("opacity", 0);
            }

            // Find if cursor is over a valid target class (not current class, not the other end)
            const potentialTarget = this.findClassAtPoint(x, y, currentClass);
            const validTarget = potentialTarget && potentialTarget !== otherClass ? potentialTarget : null;

            // Update state and notify parent for class highlighting
            if (validTarget !== this.state.reconnectTargetClass) {
                // Check compatibility with domain/range constraints
                const isCompatible = validTarget && this.props.checkReconnectCompatibility
                    ? this.props.checkReconnectCompatibility(validTarget, isSource)
                    : true;

                this.setState({
                    reconnectTargetClass: validTarget,
                    reconnectIsCompatible: isCompatible
                });
                this.props.onClassHover?.(validTarget);
            }

            // Get the other anchor position (stays fixed)
            const otherAnchor = isSource ? relationship.getTargetAnchor() : relationship.getSourceAnchor();

            // If hovering over a valid target, snap to its border
            let cursorPoint: Point;
            if (validTarget) {
                const snappedAnchor = constrainToClassBorder(
                    x, y,
                    validTarget.x, validTarget.y,
                    validTarget.width, validTarget.height
                );
                cursorPoint = { x: snappedAnchor.x, y: snappedAnchor.y };
            } else {
                cursorPoint = { x, y };
            }

            this.setState({ reconnectCursorPosition: cursorPoint });

            // Update the ghost line from the other anchor to cursor
            this.updateGhostLine(otherAnchor, cursorPoint);

            // Move the dragged anchor to follow cursor (or snap to target class border)
            const ref = isSource ? this.sourceAnchorRef : this.targetAnchorRef;
            d3.select(ref.current)
                .attr("transform", `translate(${cursorPoint.x}, ${cursorPoint.y})`);
        }
    }

    handleAnchorDragEnd(event: d3.D3DragEvent<SVGGElement, unknown, unknown>, isSource: boolean) {
        console.debug(`Anchor drag end: ${isSource ? 'source' : 'target'}, reconnectMode: ${this.state.reconnectMode}, dragDistance: ${this.dragDistance.toFixed(1)}`);

        const relationship = this.props.relationship;
        const currentClass = isSource ? relationship.source : relationship.target;

        // D3 drag event.x and event.y are already in the correct coordinate system
        const x = event.x;
        const y = event.y;

        // Clear class hover highlight
        this.props.onClassHover?.(null);

        // If drag distance is below threshold AND Ctrl/Cmd is held, reset anchor to default
        const sourceEvent = event.sourceEvent as MouseEvent | undefined;
        const isModifierHeld = sourceEvent?.ctrlKey || sourceEvent?.metaKey;

        if (this.dragDistance < DiagramRelationshipElement.DRAG_THRESHOLD && isModifierHeld) {
            console.debug(`Anchor Ctrl/Cmd+click detected - resetting to default`);

            // Reset the anchor to null (will use computed default)
            if (isSource) {
                relationship.sourceAnchor = null;
            } else {
                relationship.targetAnchor = null;
            }

            // Notify parent of anchor change
            if (this.props.onAnchorMoved) {
                const newAnchor = isSource ? relationship.getSourceAnchor() : relationship.getTargetAnchor();
                this.props.onAnchorMoved(relationship, isSource, newAnchor);
            }

            // Reset drag tracking
            this.dragStartPosition = null;
            this.dragDistance = 0;

            // Reset state and refresh
            this.setState({
                draggingAnchor: null,
                sourceAnchorHovered: false,
                targetAnchorHovered: false,
                reconnectMode: false,
                reconnectTargetClass: null,
                reconnectCursorPosition: null
            });
            this.refresh();
            return;
        }

        // Reset drag tracking
        this.dragStartPosition = null;
        this.dragDistance = 0;

        if (this.state.reconnectMode && this.state.reconnectTargetClass) {
            // RECONNECT MODE: dropped on a valid target class
            console.debug(`Reconnecting ${isSource ? 'source' : 'target'} to class: ${this.state.reconnectTargetClass.modelElement.name}`);

            // Notify parent to perform the reconnection
            if (this.props.onAnchorReconnected) {
                this.props.onAnchorReconnected(relationship, isSource, this.state.reconnectTargetClass);
            }
        } else if (this.state.reconnectMode) {
            // RECONNECT MODE but no valid target: cancel and revert
            console.debug(`Reconnection cancelled - no valid target`);
            // Just refresh to restore original positions
        } else {
            // REPOSITION MODE: update anchor position on current class
            const newAnchor = constrainToClassBorder(
                x, y,
                currentClass.x, currentClass.y,
                currentClass.width, currentClass.height
            );

            // Update the relationship anchor
            if (isSource) {
                relationship.setSourceAnchor(newAnchor);
            } else {
                relationship.setTargetAnchor(newAnchor);
            }

            // Notify parent of anchor change
            if (this.props.onAnchorMoved) {
                this.props.onAnchorMoved(relationship, isSource, newAnchor);
            }
        }

        // Restore main relationship path visibility (in case we were in reconnect mode)
        d3.select(this.svgRelationship.current).select("path.visual")
            .style("opacity", 1);
        d3.select(this.svgRelationship.current).select("path.hit")
            .style("opacity", 1);
        d3.select(this.svgRelationship.current).select("g.relationship-label")
            .style("opacity", 1);

        // Reset all drag state
        this.setState({
            draggingAnchor: null,
            sourceAnchorHovered: false,
            targetAnchorHovered: false,
            reconnectMode: false,
            reconnectTargetClass: null,
            reconnectCursorPosition: null
        });

        this.refresh();
    }

    updatePathDuringDrag(isSource: boolean, draggedAnchor: AnchorPosition) {
        const relationship = this.props.relationship;

        // Get the other anchor (not being dragged)
        const otherAnchor = isSource ? relationship.getTargetAnchor() : relationship.getSourceAnchor();

        const sourcePoint = isSource ? draggedAnchor : otherAnchor;
        const targetPoint = isSource ? otherAnchor : draggedAnchor;

        // Generate path
        const d = this.generatePath(sourcePoint, targetPoint);

        // Update path elements
        d3.select(this.svgRelationship.current).select("path.visual")
            .attr("d", d);

        d3.select(this.svgRelationship.current).select("path.hit")
            .attr("d", d);

        // Update label position during drag (slightly above center)
        const centerX = (sourcePoint.x + targetPoint.x) / 2;
        const centerY = (sourcePoint.y + targetPoint.y) / 2 - 2;

        const labelGroup = d3.select(this.svgRelationship.current).select("g.relationship-label");
        const labelText = labelGroup.select("text.label-text");
        const labelBackground = labelGroup.select("rect.label-background");

        labelText.attr("x", centerX).attr("y", centerY);

        const textNode = labelText.node() as SVGTextElement;
        if (textNode) {
            const bbox = textNode.getBBox();
            const paddingX = 8;
            const paddingY = 4;
            labelBackground
                .attr("x", centerX - bbox.width / 2 - paddingX)
                .attr("y", centerY - bbox.height / 2 - paddingY)
                .attr("width", bbox.width + paddingX * 2)
                .attr("height", bbox.height + paddingY * 2);
        }
    }

    // Constants for multiple edge visual separation
    static readonly CURVE_OFFSET = 25; // pixels of curve deviation at midpoint (for S-curves)
    static readonly ANCHOR_OFFSET = 10; // pixels between anchor attachment points

    /**
     * Apply offset to anchor position for multiple edges between same nodes.
     *
     * For arc style: Offset is perpendicular to the chord (line between nodes),
     * in the direction the arc curves. This matches the arc's position in the melon.
     *
     * For S-curve style: Offset is along the class border (perpendicular to anchor side).
     */
    getOffsetAnchor(baseAnchor: AnchorPosition, isSource: boolean): AnchorPosition {
        const edgeIndex = this.props.edgeIndex ?? 0;
        const edgeTotal = this.props.edgeTotal ?? 1;

        if (edgeTotal <= 1) {
            return baseAnchor;
        }

        if (this.props.relationshipStyle === 'arc') {
            // For arc style: offset along the class border in the direction that matches
            // the arc's position in the melon (perpendicular to chord direction)
            const relationship = this.props.relationship;

            // Get the class this anchor is on
            const anchorClass = isSource ? relationship.source : relationship.target;

            // Calculate chord direction (from source center to target center)
            const sourceClass = relationship.source;
            const targetClass = relationship.target;
            const sourceCenterX = sourceClass.x + sourceClass.width / 2;
            const sourceCenterY = sourceClass.y + sourceClass.height / 2;
            const targetCenterX = targetClass.x + targetClass.width / 2;
            const targetCenterY = targetClass.y + targetClass.height / 2;

            const dx = targetCenterX - sourceCenterX;
            const dy = targetCenterY - sourceCenterY;
            const chordLength = Math.sqrt(dx * dx + dy * dy) || 1;

            // Perpendicular direction to the chord
            const perpX = -dy / chordLength;
            const perpY = dx / chordLength;

            // Calculate sweep flag using same logic as generateArcPath
            const sourceUri = relationship.source.modelElement.uri;
            const targetUri = relationship.target.modelElement.uri;
            const isCanonicalDirection = sourceUri < targetUri;
            const baseSweep = edgeIndex % 2;  // 0 for even, 1 for odd
            const sweepFlag = isCanonicalDirection ? baseSweep : (1 - baseSweep);

            // Layer determines offset magnitude (outer arcs need larger offset)
            const layer = Math.floor(edgeIndex / 2);
            const layerOffset = (layer + 1) * DiagramRelationshipElement.ANCHOR_OFFSET;

            // sweepFlag determines direction: 0 = positive perpendicular, 1 = negative
            const sign = sweepFlag === 0 ? 1 : -1;

            // Project the perpendicular offset onto the class border
            // For left/right anchors, we can only move along Y (vertical border)
            // For top/bottom anchors, we can only move along X (horizontal border)
            let offset: number;
            if (baseAnchor.side === 'left' || baseAnchor.side === 'right') {
                // Vertical border: project perpendicular onto Y axis
                offset = perpY * layerOffset * sign;
                const newY = Math.max(
                    anchorClass.y,
                    Math.min(anchorClass.y + anchorClass.height, baseAnchor.y + offset)
                );
                return { ...baseAnchor, y: newY };
            } else {
                // Horizontal border: project perpendicular onto X axis
                offset = perpX * layerOffset * sign;
                const newX = Math.max(
                    anchorClass.x,
                    Math.min(anchorClass.x + anchorClass.width, baseAnchor.x + offset)
                );
                return { ...baseAnchor, x: newX };
            }
        } else {
            // For S-curves: offset along the class border (perpendicular to anchor side)
            const spread = (edgeTotal - 1) * DiagramRelationshipElement.ANCHOR_OFFSET;
            const offset = (edgeIndex * DiagramRelationshipElement.ANCHOR_OFFSET) - (spread / 2);

            if (baseAnchor.side === 'left' || baseAnchor.side === 'right') {
                return { ...baseAnchor, y: baseAnchor.y + offset };
            } else {
                return { ...baseAnchor, x: baseAnchor.x + offset };
            }
        }
    }

    /**
     * Generate path based on relationship style.
     * - 's-curve': Uses D3's link generators for natural S-shaped curves
     * - 'arc': Uses circular arc paths (typical in graph visualization tools)
     */
    generatePath(sourceAnchor: AnchorPosition, targetAnchor: AnchorPosition): string {
        const style = this.props.relationshipStyle ?? 's-curve';

        if (style === 'arc') {
            return this.generateArcPath(sourceAnchor, targetAnchor);
        }
        return this.generateSCurvePath(sourceAnchor, targetAnchor);
    }

    /**
     * Generate S-curve path using D3's link generators.
     */
    generateSCurvePath(sourceAnchor: AnchorPosition, targetAnchor: AnchorPosition): string {
        const source = { x: sourceAnchor.x, y: sourceAnchor.y };
        const target = { x: targetAnchor.x, y: targetAnchor.y };

        // Use D3's link generators for natural S-shaped curves
        const useHorizontal = sourceAnchor.side === 'left' || sourceAnchor.side === 'right';

        const linkGenerator = useHorizontal
            ? d3.linkHorizontal<any, Point>().x(d => d.x).y(d => d.y)
            : d3.linkVertical<any, Point>().x(d => d.x).y(d => d.y);

        return linkGenerator({ source, target }) || `M ${source.x} ${source.y} L ${target.x} ${target.y}`;
    }

    /**
     * Generate circular arc path.
     * For multiple edges between the same nodes, creates a "melon shape":
     * - Arcs alternate direction (sweep flag) based on edge index
     * - Arc height increases for edges further from center
     * - This creates a lens/eye pattern where edges fan out on both sides
     */
    generateArcPath(sourceAnchor: AnchorPosition, targetAnchor: AnchorPosition): string {
        const source = { x: sourceAnchor.x, y: sourceAnchor.y };
        const target = { x: targetAnchor.x, y: targetAnchor.y };

        const edgeIndex = this.props.edgeIndex ?? 0;
        const edgeTotal = this.props.edgeTotal ?? 1;

        // Calculate the chord length (distance between source and target)
        const dx = target.x - source.x;
        const dy = target.y - source.y;
        const chordLength = Math.sqrt(dx * dx + dy * dy);

        // If points are very close, just draw a straight line
        if (chordLength < 10) {
            return `M ${source.x} ${source.y} L ${target.x} ${target.y}`;
        }

        // Determine canonical direction using URI comparison
        // This ensures consistent arc assignment regardless of edge direction
        const relationship = this.props.relationship;
        const sourceUri = relationship.source.modelElement.uri;
        const targetUri = relationship.target.modelElement.uri;
        const isCanonicalDirection = sourceUri < targetUri;

        let arcHeight: number;
        let sweepFlag: number;

        if (edgeTotal === 1) {
            // Single edge: very subtle arc for visual appeal (5% of chord)
            arcHeight = chordLength * 0.05;
            sweepFlag = 0;
        } else {
            // Multiple edges: create melon shape with alternating directions and increasing heights
            //
            // For melon shape, we distribute edges on both sides:
            // - Even indices (0, 2, 4...) go on one side (sweep = 0)
            // - Odd indices (1, 3, 5...) go on the other side (sweep = 1)
            //
            // Height increases based on how far from center:
            // - Index 0, 1: layer 0 (smallest height)
            // - Index 2, 3: layer 1 (medium height)
            // - Index 4, 5: layer 2 (larger height)
            // etc.

            const layer = Math.floor(edgeIndex / 2);  // Which "layer" of the melon (0, 1, 2...)
            const baseHeight = 25;  // Base arc height for innermost layer
            const heightIncrement = 20;  // Additional height per layer

            arcHeight = baseHeight + (layer * heightIncrement);

            // Cap at reasonable maximum
            const maxHeight = chordLength * 0.4;
            if (arcHeight > maxHeight) {
                arcHeight = maxHeight;
            }

            // Alternate sweep direction based on edge index
            // If edge is NOT in canonical direction, flip the sweep flag
            // This ensures consistent visual behavior regardless of edge direction
            const baseSweep = edgeIndex % 2;  // 0 for even, 1 for odd
            sweepFlag = isCanonicalDirection ? baseSweep : (1 - baseSweep);
        }

        // Calculate the radius needed for an arc with the given chord length and height
        // Using the formula: r = (c^2 + 4h^2) / (8h) where c = chord length, h = arc height
        const radius = (chordLength * chordLength + 4 * arcHeight * arcHeight) / (8 * arcHeight);

        // large-arc-flag: always 0 since we want the shorter arc
        return `M ${source.x} ${source.y} A ${radius} ${radius} 0 0 ${sweepFlag} ${target.x} ${target.y}`;
    }

    componentDidUpdate(prevProps: Readonly<Props>, _prevState: Readonly<State>, _snapshot?: any) {
        // Re-setup anchor interactions when switching from arc to s-curve mode
        // (anchors are conditionally rendered, so refs change)
        if (prevProps.relationshipStyle === 'arc' && this.props.relationshipStyle !== 'arc' && !this.props.isInferred) {
            this.setupAnchorInteractions(true);
            this.setupAnchorInteractions(false);
        }
        this.refresh();
    }

    highlight() {
        const theme = getTheme();
        d3.select(this.svgRelationship.current)
            .select("path.visual")
            .attr("stroke", theme.lineHighlight);
    }

    static GetPath(diagramClass1: DiagramClass, diagramClass2 : DiagramClass) {
        const t1 = {x: diagramClass1.x, y: diagramClass1.y};
        const t2 = {x: diagramClass2.x, y: diagramClass2.y};

        // Get actual dimensions from diagram classes
        const w1 = diagramClass1.width;
        const h1 = diagramClass1.height;
        const w2 = diagramClass2.width;
        const h2 = diagramClass2.height;

        // calculate angle between t1 and t2
        const angle = Math.atan2(t2.y - t1.y, t2.x - t1.x);

        let source: Point;
        let target: Point;
        let linkGenerator: d3.Link<any, any, any>;

        if (angle > -0.8 && angle < 0.8) {
            // Target is to the right: source from right edge, target on left edge
            source = {x: t1.x + w1, y: Math.max(Math.min(t2.y + h2/2, t1.y + h1 - 25), t1.y + 25)};
            target = {x: t2.x, y: t2.y + h2/2};
            linkGenerator = d3.linkHorizontal()
                .x((d: any) => d.x)
                .y((d: any) => d.y);
        } else if (angle > 0.8 && angle < 2.33) {
            // Target is below: source from bottom edge, target on top edge
            source = {x: Math.max(Math.min(t2.x + w2/2, t1.x + w1 - 25), t1.x + 25), y: t1.y + h1};
            target = {x: t2.x + w2/2, y: t2.y};
            linkGenerator = d3.linkVertical()
                .x((d: any) => d.x)
                .y((d: any) => d.y);
        } else if (angle > -2.33 && angle < -0.8) {
            // Target is above: source from top edge, target on bottom edge
            source = {x: Math.max(Math.min(t2.x + w2/2, t1.x + w1 - 25), t1.x + 25), y: t1.y};
            target = {x: t2.x + w2/2, y: t2.y + h2};
            linkGenerator = d3.linkVertical()
                .x((d: any) => d.x)
                .y((d: any) => d.y);
        } else {
            // Target is to the left: source from left edge, target on right edge
            source = {x: t1.x, y: Math.max(Math.min(t2.y + h2/2, t1.y + h1 - 25), t1.y + 25)};
            target = {x: t2.x + w2, y: t2.y + h2/2};
            linkGenerator = d3.linkHorizontal()
                .x((d: any) => d.x)
                .y((d: any) => d.y);
        }

        return linkGenerator({source: source, target: target});
    }

    // createSvg() {
    //     this.svgRelationship = d3.select(this.diagram.svgClasses.current).append("g")
    //         .attr("target", "diagramRelationship");
    //
    //
    //     const visual = this.svgRelationship.append("path")
    //         .attr("target", "visual")
    //         .attr("stroke-width", 1)
    //         .attr("fill", "none")
    //         .attr("marker-start", `url(#${relationshipMarkers.get(this.type)})`);
    //
    //     const hit = this.svgRelationship.append("path")
    //         .attr("target", "hit")
    //         .attr("stroke-width", 15)
    //         .attr("fill", "none")
    //         .attr("stroke", "white")
    //         .attr("stroke-opacity", 0);
    //
    //     const diagramRelationship = this;
    //     hit.on("mouseover", function () {
    //         //console.log("mouseover");
    //         visual.attr("stroke", theme.lineHighlight);
    //
    //     }).on("mouseout", function () {
    //         //console.log("mouseout");
    //         diagramRelationship.refresh();
    //     }).on("click", function () {
    //         diagramRelationship.diagram.selectRelationship(diagramRelationship);
    //     });
    //
    //     return this.svgRelationship;
    // }

    refresh() {
        const theme = getTheme();
        const relationship = this.props.relationship;
        const type = relationship.modelRelationship.type as RELATIONSHIP_TYPE;
        const dasharray = getStrokeDasharray(type);
        const { sourceMarker, targetMarker } = this.getMarkers();

        // Get anchor positions (computed or stored), then apply offset for multiple edges
        // For 'arc' style, always use computed anchors (ignore fixed positions)
        const anchorMode = this.props.anchorMode ?? 'auto';
        const isArcStyle = this.props.relationshipStyle === 'arc';
        const baseSourceAnchor = relationship.getSourceAnchor(anchorMode, isArcStyle);
        const baseTargetAnchor = relationship.getTargetAnchor(anchorMode, isArcStyle);
        const sourceAnchor = this.getOffsetAnchor(baseSourceAnchor, true);
        const targetAnchor = this.getOffsetAnchor(baseTargetAnchor, false);

        // Skip path/label updates during reconnect mode (they're hidden anyway)
        if (!this.state.reconnectMode) {
            // Generate path using anchors
            const d = this.generatePath(sourceAnchor, targetAnchor);

            // Main visual path
            const visual = d3.select(this.svgRelationship.current).select("path.visual")
                .attr("d", d)
                .attr("stroke", theme.line)
                .attr("stroke-width", 2)
                .attr("stroke-linecap", "round")
                .attr("stroke-dasharray", dasharray !== "none" ? dasharray : null)
                .attr("marker-start", sourceMarker ? `url(#${sourceMarker})` : null)
                .attr("marker-end", targetMarker ? `url(#${targetMarker})` : null);

            // Update hit path for interaction
            d3.select(this.svgRelationship.current).select("path.hit")
                .attr("d", d);

            // Update label position at center of path (accounting for curve offset)
            const edgeIndex = this.props.edgeIndex ?? 0;
            const edgeTotal = this.props.edgeTotal ?? 1;
            const style = this.props.relationshipStyle ?? 's-curve';
            let centerX = (sourceAnchor.x + targetAnchor.x) / 2;
            let centerY = (sourceAnchor.y + targetAnchor.y) / 2 - 2;

            // Calculate label offset based on relationship style
            const dx = targetAnchor.x - sourceAnchor.x;
            const dy = targetAnchor.y - sourceAnchor.y;
            const chordLength = Math.sqrt(dx * dx + dy * dy) || 1;
            const perpX = -dy / chordLength;
            const perpY = dx / chordLength;

            if (style === 'arc') {
                // For arc curves, position label at the arc's apex
                // Must match the arc height and sweep flag calculation in generateArcPath()

                // Determine canonical direction using URI comparison (same as generateArcPath)
                const sourceUri = relationship.source.modelElement.uri;
                const targetUri = relationship.target.modelElement.uri;
                const isCanonicalDirection = sourceUri < targetUri;

                let arcHeight: number;
                let sweepFlag: number;

                if (edgeTotal === 1) {
                    // Single edge: subtle arc (5% of chord)
                    arcHeight = chordLength * 0.05;
                    sweepFlag = 0;
                } else {
                    // Multiple edges: melon shape with alternating directions and increasing heights
                    const layer = Math.floor(edgeIndex / 2);
                    const baseHeight = 25;
                    const heightIncrement = 20;

                    arcHeight = baseHeight + (layer * heightIncrement);

                    const maxHeight = chordLength * 0.4;
                    if (arcHeight > maxHeight) {
                        arcHeight = maxHeight;
                    }

                    // Alternate sweep direction (same logic as generateArcPath)
                    const baseSweep = edgeIndex % 2;
                    sweepFlag = isCanonicalDirection ? baseSweep : (1 - baseSweep);
                }

                // Calculate perpendicular offset direction based on sweep flag
                // sweepFlag 0 = counterclockwise = perpendicular points "left" of direction
                // sweepFlag 1 = clockwise = perpendicular points "right" of direction
                const sign = sweepFlag === 0 ? 1 : -1;

                // Position label at the arc apex
                centerX += perpX * arcHeight * sign;
                centerY += perpY * arcHeight * sign;
            } else if (edgeTotal > 1) {
                // For S-curves with multiple edges, offset the label to follow the curve
                const spread = (edgeTotal - 1) * DiagramRelationshipElement.CURVE_OFFSET;
                const curveOffset = (edgeIndex * DiagramRelationshipElement.CURVE_OFFSET) - (spread / 2);

                // For quadratic bezier, the midpoint of the curve is at 0.5 * control point offset
                centerX += perpX * curveOffset * 0.5;
                centerY += perpY * curveOffset * 0.5;
            }

            const labelGroup = d3.select(this.svgRelationship.current).select("g.relationship-label");
            const labelText = labelGroup.select("text.label-text");
            const labelBackground = labelGroup.select("rect.label-background");

            // Position the text
            labelText.attr("x", centerX).attr("y", centerY);

            // Get text bounding box and size background accordingly
            const textNode = labelText.node() as SVGTextElement;
            if (textNode) {
                const bbox = textNode.getBBox();
                const paddingX = 8;
                const paddingY = 4;
                labelBackground
                    .attr("x", centerX - bbox.width / 2 - paddingX)
                    .attr("y", centerY - bbox.height / 2 - paddingY)
                    .attr("width", bbox.width + paddingX * 2)
                    .attr("height", bbox.height + paddingY * 2);
            }

            // Check if this edge is an outgoing edge from the selected node
            const isOutgoingFromSelected = this.props.selectedNodeUri &&
                relationship.source.modelElement.uri === this.props.selectedNodeUri;

            // Check if a node is selected but this edge is NOT outgoing from it (should be dimmed)
            const shouldDim = this.props.selectedNodeUri && !isOutgoingFromSelected;

            // Check for predicate highlighting from WordCloud
            const predicateUri = relationship.modelRelationship.predicate.uri;
            const predicateHighlightColor = this.props.highlightedPredicates?.get(predicateUri);

            // State-based styling
            // Predicate highlighting takes priority (from WordCloud panel)
            if (predicateHighlightColor) {
                visual.attr("stroke", predicateHighlightColor)
                      .attr("stroke-width", 3)
                      .attr("opacity", 1);
            } else if (this.props.isInferred) {
                // Inferred edges can also be highlighted when outgoing from selected node
                if (isOutgoingFromSelected) {
                    visual.attr("stroke", INFERRED_RELATIONSHIP_COLOR)
                          .attr("stroke-width", 3)
                          .attr("opacity", 1);
                } else if (shouldDim) {
                    visual.attr("stroke", INFERRED_RELATIONSHIP_COLOR)
                          .attr("stroke-width", 2)
                          .attr("opacity", 0.25);
                } else {
                    visual.attr("stroke", INFERRED_RELATIONSHIP_COLOR)
                          .attr("stroke-width", 2)
                          .attr("opacity", 0.85);
                }
            } else if (this.props.selected || isOutgoingFromSelected) {
                // Highlight selected relationship or outgoing edges from selected node
                visual.attr("stroke", theme.lineHighlight)
                      .attr("stroke-width", 3)
                      .attr("opacity", 1);
            } else if (this.state.relationshipHovered || this.props.highlightedClass == relationship.source || d3.select(this.svgRelationship.current).classed("selected")) {
                visual.attr("stroke", theme.lineHighlight)
                      .attr("stroke-width", 2)
                      .attr("opacity", 1);
            } else if (shouldDim) {
                // Dim non-outgoing edges when a node is selected
                visual.attr("stroke", theme.line)
                      .attr("stroke-width", 2)
                      .attr("opacity", 0.25);
            } else {
                visual.attr("stroke", theme.line)
                      .attr("stroke-width", 2)
                      .attr("opacity", 1);
            }

            // Apply label styling to match edge visibility
            if (predicateHighlightColor) {
                // Highlight label with predicate color from WordCloud
                labelGroup.attr("opacity", 1);
                labelBackground.attr("fill", predicateHighlightColor);
                labelText.style("fill", "white");
            } else if (shouldDim) {
                labelGroup.attr("opacity", 0.25);
                labelBackground.attr("fill", "rgba(40, 42, 50, 0.9)");
                labelText.style("fill", "rgba(255, 255, 255, 0.75)");
            } else if (this.props.selected || isOutgoingFromSelected) {
                labelGroup.attr("opacity", 1);
                // Brighten label background and text for selected/highlighted edges
                labelBackground.attr("fill", "rgba(80, 160, 180, 0.95)");
                labelText.style("fill", "rgba(255, 255, 255, 1)");
            } else {
                labelGroup.attr("opacity", 1);
                labelBackground.attr("fill", "rgba(40, 42, 50, 0.9)");
                labelText.style("fill", "rgba(255, 255, 255, 0.75)");
            }
        }

        // Update anchor positions - but skip the one being dragged
        if (this.state.draggingAnchor !== 'source') {
            d3.select(this.sourceAnchorRef.current)
                .attr("transform", `translate(${sourceAnchor.x}, ${sourceAnchor.y})`);
        }

        if (this.state.draggingAnchor !== 'target') {
            d3.select(this.targetAnchorRef.current)
                .attr("transform", `translate(${targetAnchor.x}, ${targetAnchor.y})`);
        }

        // Update anchor visuals based on selection state
        this.updateAnchorVisuals(true, this.state.sourceAnchorHovered);
        this.updateAnchorVisuals(false, this.state.targetAnchorHovered);

        // Update anchor visibility
        // Hide anchors when arc style is selected (fixed positions are disabled for arcs)
        const shouldShowAnchors = !isArcStyle && (this.props.selected || this.state.relationshipHovered || this.state.draggingAnchor !== null);
        this.updateAnchorsVisibility(shouldShowAnchors, isArcStyle);
    }
}

export default DiagramRelationshipElement;